import { Conversation } from '@/modules/messenger/_modules/conversation/models/Conversation';
import { ModelCollection } from '@/modules/core/app/models/ModelCollection';
import { ConversationUser } from '@/modules/messenger/_modules/conversation/models/ConversationUser';

export class ConversationCollection extends ModelCollection<Conversation> {
  constructor(items: Conversation[] | null = null) {
    super(items, Conversation);
  }

  static mapModel(items: Conversation[] | null = null): ConversationCollection {
    return new this(items);
  }

  getOrderedItems(): Conversation[] | undefined {
    return this.items?.sort((a: Conversation, b: Conversation): number =>
      a.isGeneral ? -1 : b.isGeneral ? 1 : 0
    );
  }

  get visibleConversations(): Conversation[] | undefined {
    return this.items?.filter((conversation: Conversation) => conversation.isVisible);
  }

  get hasVisibleConversation(): boolean {
    return !!this.items?.some((conversation: Conversation) => conversation.isVisible);
  }

  get hasOnlyGeneralConversation(): boolean {
    return this.items?.length === 1 && this.items[0].isGeneral;
  }

  getActiveConversations(userId: number): ConversationCollection {
    return ConversationCollection.mapModel(
      this.items?.filter(
        (conversation: Conversation) =>
          conversation.isUnread(userId) ||
          (!conversation.isArchived &&
            !conversation.hasDraftMessage &&
            !conversation.isUnread(userId))
      )
    );
  }

  getUnreadConversations(userId: number): ConversationCollection {
    return ConversationCollection.mapModel(
      this.items?.filter((conversation: Conversation) => conversation.isUnread(userId))
    );
  }

  getReadConversations(userId: number): ConversationCollection {
    return ConversationCollection.mapModel(
      this.items?.filter((conversation: Conversation) => !conversation.isUnread(userId))
    );
  }

  getDraftConversations(userId: number): ConversationCollection {
    return ConversationCollection.mapModel(
      this.items?.filter(
        (conversation: Conversation) =>
          !conversation.isArchived && conversation.hasDraftMessage && !conversation.isUnread(userId)
      )
    );
  }

  getOtherConversations(userId: number): ConversationCollection {
    return ConversationCollection.mapModel(
      this.items?.filter(
        (conversation: Conversation) =>
          !conversation.isArchived &&
          !conversation.hasDraftMessage &&
          !conversation.isUnread(userId)
      )
    );
  }

  getArchivedConversations(): ConversationCollection {
    return ConversationCollection.mapModel(
      this.items?.filter((conversation: Conversation) => conversation.isArchived)
    );
  }

  getExistingConversation(conversation: Conversation): Conversation | undefined {
    if (!this.items || !conversation.conversationUsers) {
      return;
    }

    const userIds: number[] = conversation.conversationUsers.map(
      (conversationUser: ConversationUser) => conversationUser.userId as number
    );

    return this.items
      .filter((conversation: Conversation) => !conversation.isArchived)
      .find(
        (item: Conversation) =>
          item.conversationUsers?.length === userIds.length &&
          item.conversationUsers?.every((conversationUser: ConversationUser) =>
            userIds.includes(conversationUser.userId as number)
          )
      );
  }
}
